import {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react';
import moment from 'moment-timezone';
import { RightPanelCloseIcon } from 'src/icons/RightPanelClose';
import { RightPanelOpenIcon } from 'src/icons/RightPanelOpen';
import { useSelector } from 'react-redux';
import { SegmentedTabs } from './SegmentedTabs';
import {
  EditOutlined,
  UploadOutlined,
  InfoCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  PlusOutlined,
  SendOutlined,
} from '@ant-design/icons';
import {
  Card,
  Input,
  Tooltip,
  Switch,
  Modal,
  Skeleton,
  Checkbox,
  Button,
  message,
} from 'antd';
import { useAuth, useFeatureEntitlements } from '@frontegg/react';
import { ProIcon } from 'src/icons/Pro.icon';
import { LockIcon } from 'src/icons/Lock.icon';
import { isEmpty } from 'lodash';
import { UpgradeTooltip } from '../Payments/UpgradeTooltip';
import { getAudio } from '../../store/actions/audio';
import { useDispatch } from 'react-redux';
import AudioPlayer from 'src/components/baseComponents/AudioStream';
import { getEncounterAudioSlice } from '../../store/slices/audio';
import { AudioUploadModal } from './AudioUploadModal';
import { getFileName } from '../../store/actions/audio';
import { getFileNameSlice } from '../../store/slices/audio';
import { updateCurrentPractitionerSettings } from 'src/store/actions/practitioner.action';
import { listSummaries, regenerateSummary } from '../../store/actions/summary';
import { usePolling } from '../../hooks/usePolling';
import { analytics } from 'src/services';
import { sanitizeHtmlContent } from 'src/utils';
import DOMPurify from 'dompurify';
import { ICD10Coding } from './ICD10Coding';

const CPTCodes = ({ encounterStatus, allowed }) => {
  const { cptCodesSummary, cptCodesUpdatedAt } = useSelector(
    (state) => state.summaryState
  );
  const { encounterDetails } = useSelector((state) => state?.encounterState);
  const dispatch = useDispatch();

  const calculateRefreshInterval = useCallback(() => {
    const isGenerating = encounterDetails?.cpt_status === 'inprogress';
    return isGenerating ? 10000 : 60000;
  }, [encounterDetails?.cpt_status]);

  const fetchCptCodes = useCallback(() => {
    if (encounterDetails?.encounter_id) {
      dispatch(listSummaries(encounterDetails.encounter_id, 'in-visit', 'cpt'));
    }
  }, [dispatch, encounterDetails?.encounter_id]);

  usePolling(
    {
      pollingFunction: fetchCptCodes,
      calculateInterval: calculateRefreshInterval,
    },
    [encounterDetails?.cpt_status]
  );

  if (!allowed) {
    return (
      <div className="text-gray-400 p-4">
        CPT coding feature is not enabled for this account.
      </div>
    );
  }

  if (encounterStatus != 'completed') {
    return (
      <div className="text-gray-400 p-4">
        CPT codes will appear shortly after the note is generated.
      </div>
    );
  }

  return (
    <div className="p-4">
      {cptCodesUpdatedAt && encounterDetails?.cpt_status === 'ready' && (
        <span className="text-gray-400 text-xs flex justify-center mb-2">
          Last updated {moment(cptCodesUpdatedAt).fromNow()}
        </span>
      )}
      {cptCodesSummary?.codes?.map((summary, index) => {
        return (
          <Card
            key={index}
            title={
              <span className="text-lg" style={{ color: 'rgb(0, 208, 145)' }}>
                {summary.name}
              </span>
            }
            className="mb-4 "
          >
            <div className="font-medium" style={{ color: 'black' }}>
              Description
            </div>
            <p
              className="text-gray-600 text-xs"
              style={{ marginBottom: '10px' }}
            >
              {summary.description}
            </p>
          </Card>
        );
      })}
      {encounterDetails?.cpt_status === 'ready' &&
        !cptCodesSummary?.codes?.length && (
          <div className="text-gray-400">
            No CPT codes found for this encounter.
          </div>
        )}
      {encounterDetails?.cpt_status !== 'ready' && (
        <div className="text-gray-400">Extracting CPT codes...</div>
      )}
    </div>
  );
};

const PrerecordedTranscriptionDiarizedView = ({
  prerecordedTranscript,
  prerecordedTranscriptSpeakers,
  browserTimezone,
  placeholder,
  selectedEvidences,
  onEditTranscription,
}) => {
  // Sort in ascending order (oldest first) using seconds
  const sortedTranscripts = [...prerecordedTranscript].sort(
    (a, b) => a.time - b.time
  );
  const { isEntitled: canEditTranscription } =
    useFeatureEntitlements('edit-transcription');
  const speakerColors = ['#00D090', '#FF6B6B', '#4ECDC4', '#FFA07A', '#45B7D1'];
  const [editingIndex, setEditingIndex] = useState(null);
  const [editText, setEditText] = useState('');
  const { user } = useAuth();

  const handleEdit = (index, text) => {
    analytics.track('Clicked Edit Prerecorded Transcription', {
      email: user?.email,
    });
    setEditingIndex(index);
    setEditText(text);
  };

  const handleSave = (index, time, speaker) => {
    analytics.track('Clicked Save Prerecorded Transcription', {
      email: user?.email,
    });
    const newTranscript = sortedTranscripts.map((item, i) =>
      i === index ? { ...item, text: editText } : item
    );
    onEditTranscription('prerecorded', newTranscript);
    setEditingIndex(null);
    setEditText('');
  };

  const handleCancel = () => {
    analytics.track('Clicked Cancel Prerecorded Transcription', {
      email: user?.email,
    });
    setEditingIndex(null);
    setEditText('');
  };

  const handleDelete = (index) => {
    analytics.track('Clicked Delete Prerecorded Transcription Segment', {
      email: user?.email,
    });
    const newTranscript = [...prerecordedTranscript];
    const reversedIndex = prerecordedTranscript.length - 1 - index;
    newTranscript.splice(reversedIndex, 1);
    onEditTranscription('prerecorded', newTranscript);
  };

  // Format seconds to MM:SS
  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Group by minute and maintain speaker alternation order
  const groupedTranscripts = sortedTranscripts.reduce((acc, transcript) => {
    const timeLabel = formatDuration(transcript.time);

    if (!acc[timeLabel]) {
      acc[timeLabel] = [];
    }

    const speakerKey = transcript.speaker?.toString() || 'Unknown';
    const lastMessage = acc[timeLabel][acc[timeLabel].length - 1];

    if (lastMessage && lastMessage.speaker === speakerKey) {
      lastMessage.text += ' ' + transcript.text;
    } else {
      acc[timeLabel].push({
        text: transcript.text,
        speaker: speakerKey,
        time: transcript.time,
      });
    }
    return acc;
  }, {});

  return (
    <div className="chat-view p-2">
      {Object.entries(groupedTranscripts).map(([timeLabel, messages]) => (
        <div key={timeLabel} className="mb-6">
          <div className="text-center mb-4">
            <span className="bg-gray-200 text-gray-600 text-xs font-semibold px-2 py-1 rounded-full">
              {timeLabel}
            </span>
          </div>
          {messages.map((message, index) => {
            const speakerKey = message.speaker;
            const speakerName =
              prerecordedTranscriptSpeakers?.[speakerKey] ||
              `Speaker ${speakerKey}`;

            const selectedEvidence = selectedEvidences.find(
              (evidence) =>
                String(evidence.timestamp) === String(message.time) &&
                String(evidence.speaker) === String(speakerKey)
            );

            const displayText = selectedEvidence ? (
              <div
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtmlContent(selectedEvidence.text),
                }}
              />
            ) : (
              message.text
            );

            return (
              <div key={index} className="flex justify-start mb-2">
                <div
                  className={`w-full rounded-lg p-3 ${
                    speakerKey === '0'
                      ? 'bg-gray-200 text-gray-800'
                      : 'bg-gray-100 text-gray-800'
                  } ${selectedEvidence ? 'border-2 border-yellow-500' : ''}`}
                >
                  <div className="flex justify-between items-start">
                    <div
                      className="font-semibold mb-1 text-xs"
                      style={{
                        color:
                          speakerColors[
                            parseInt(speakerKey) % speakerColors.length
                          ],
                      }}
                    >
                      {speakerName}
                    </div>
                    {canEditTranscription && (
                      <div className="flex gap-1">
                        {editingIndex === index ? (
                          <>
                            <button
                              onClick={() =>
                                handleSave(index, message.time, speakerKey)
                              }
                              className="text-[#00D090] hover:text-[#059669]"
                            >
                              <CheckOutlined />
                            </button>
                            <button
                              onClick={handleCancel}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              <CloseOutlined />
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => handleEdit(index, message.text)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <EditOutlined />
                          </button>
                        )}
                        {editingIndex !== index && (
                          <button
                            onClick={() => handleDelete(index)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <DeleteOutlined />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {editingIndex === index ? (
                    <div>
                      <Input.TextArea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        autoSize
                        className="flex-grow text-xs"
                      />
                    </div>
                  ) : (
                    <div className="text-xs whitespace-pre-wrap">
                      {displayText}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}
      {!prerecordedTranscript?.length && (
        <div className="text-gray-400 text-center text-xs">{placeholder}</div>
      )}
    </div>
  );
};

const LiveTranscriptionDiarizedView = ({
  fullTranscript,
  setFullTranscript,
  livePartialTranscript,
  liveTranscriptSpeakers,
  browserTimezone,
  placeholder,
  selectedEvidences,
  onEditTranscription,
}) => {
  let isFirstSummaryEvidence = false;
  const summaryEvidenceDiv = useRef(null);
  const { isEntitled: canEditTranscription } =
    useFeatureEntitlements('edit-transcription');
  const [editingIndex, setEditingIndex] = useState(null);
  const [editText, setEditText] = useState('');
  const { user } = useAuth();
  const speakerColors = ['#00D090', '#FF6B6B', '#4ECDC4', '#FFA07A', '#45B7D1'];

  const handleEdit = (index, text) => {
    analytics.track('Clicked Edit Live Diarized Transcription', {
      email: user?.email,
    });
    setEditingIndex(index);
    setEditText(text);
  };

  const handleSave = (index) => {
    analytics.track('Clicked Save Live Diarized Transcription', {
      email: user?.email,
    });
    const newTranscript = fullTranscript.map((item, i) =>
      i === index ? { ...item, text: editText } : item
    );
    onEditTranscription('live', newTranscript);
    setFullTranscript(newTranscript);
    setEditingIndex(null);
  };

  const handleCancel = () => {
    analytics.track('Clicked Cancel Live Diarized Transcription', {
      email: user?.email,
    });
    setEditingIndex(null);
    setEditText('');
  };

  const handleDelete = (index) => {
    analytics.track('Clicked Delete Live Diarized Transcription Segment', {
      email: user?.email,
    });
    const newTranscript = fullTranscript.filter((_, i) => i !== index);
    onEditTranscription('live', newTranscript);
    setFullTranscript(newTranscript);
    // Close edit mode if the deleted item was being edited
    if (editingIndex === index) {
      setEditingIndex(null);
      setEditText('');
    }
  };

  useEffect(() => {
    if (summaryEvidenceDiv.current && selectedEvidences?.length > 0) {
      summaryEvidenceDiv.current.scrollIntoView();
    }
  }, [selectedEvidences?.length]);

  return (
    <div className="chat-view p-2">
      {fullTranscript.map((transcript, index) => {
        const speakerKey = transcript.speaker?.toString() || 'Unknown';
        const speakerName =
          liveTranscriptSpeakers?.[speakerKey] || `Speaker ${speakerKey}`;

        const selectedEvidence = selectedEvidences.find((selectedEvidence) => {
          const timestampMatch =
            moment(selectedEvidence.timestamp)
              .tz(browserTimezone)
              .format('YYYY-MM-DDTHH:mm:ss') === transcript.timestamp;
          const speakerMatch =
            String(selectedEvidence.speaker) === String(speakerKey);
          return timestampMatch && speakerMatch;
        });
        const isSelectedEvidence = !isEmpty(selectedEvidence);
        isFirstSummaryEvidence = isSelectedEvidence && !isFirstSummaryEvidence;

        let displayText = transcript.text;
        if (isSelectedEvidence) {
          displayText = (
            <div
              dangerouslySetInnerHTML={{
                __html: sanitizeHtmlContent(selectedEvidence.text),
              }}
            />
          );
        }

        return (
          <div key={index} className="flex justify-start mb-2">
            <div
              className={`w-full rounded-lg p-3 ${
                speakerKey === '0'
                  ? 'bg-gray-200 text-gray-800'
                  : 'bg-gray-100 text-gray-800'
              } ${isSelectedEvidence ? 'border-2 border-yellow-500' : ''}`}
              ref={isFirstSummaryEvidence ? summaryEvidenceDiv : null}
            >
              <div className="flex justify-between items-start">
                <div
                  className="font-semibold mb-1 text-xs"
                  style={{
                    color:
                      speakerColors[
                        parseInt(speakerKey) % speakerColors.length
                      ],
                  }}
                >
                  <span className="text-black text-xs">
                    {moment(transcript.timestamp)
                      .tz(browserTimezone)
                      .format('h:mma')}{' '}
                  </span>
                  [{speakerName}]
                </div>
                {canEditTranscription && (
                  <div className="flex gap-1">
                    {editingIndex === index ? (
                      <>
                        <button
                          onClick={() => handleSave(index)}
                          className="text-[#00D090] hover:text-[#059669]"
                        >
                          <CheckOutlined />
                        </button>
                        <button
                          onClick={handleCancel}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <CloseOutlined />
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => handleEdit(index, transcript.text)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <EditOutlined />
                      </button>
                    )}
                    {editingIndex !== index && (
                      <button
                        onClick={() => handleDelete(index)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <DeleteOutlined />
                      </button>
                    )}
                  </div>
                )}
              </div>
              {editingIndex === index ? (
                <div>
                  <Input.TextArea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    autoSize
                    className="flex-grow text-xs"
                  />
                </div>
              ) : (
                <div className="text-xs whitespace-pre-wrap">{displayText}</div>
              )}
              {index === 0 && livePartialTranscript && (
                <span className="text-gray-400 text-xs">
                  {' '}
                  {livePartialTranscript}
                </span>
              )}
            </div>
          </div>
        );
      })}
      {!fullTranscript?.length && !livePartialTranscript?.length ? (
        <div className="text-gray-400 text-center text-xs">{placeholder}</div>
      ) : null}
    </div>
  );
};

const LiveTranscriptionNonDiarizedView = ({
  fullTranscript,
  setFullTranscript,
  livePartialTranscript,
  browserTimezone,
  placeholder,
  selectedEvidences,
  onEditTranscription,
}) => {
  let isFirstSummaryEvidence = false;
  const summaryEvidenceDiv = useRef(null);
  const { isEntitled: canEditTranscription } =
    useFeatureEntitlements('edit-transcription');
  const [editingIndex, setEditingIndex] = useState(null);
  const [editText, setEditText] = useState('');
  const { user } = useAuth();

  const handleEdit = (index, text) => {
    analytics.track('Clicked Edit Live Transcription', {
      email: user?.email,
    });
    setEditingIndex(index);
    setEditText(text);
  };

  const handleSave = (index) => {
    analytics.track('Clicked Save Live Transcription', {
      email: user?.email,
    });
    const newTranscript = fullTranscript.map((item, i) =>
      i === index ? { ...item, text: editText } : item
    );
    onEditTranscription('live', newTranscript);
    setFullTranscript(newTranscript);
    setEditingIndex(null);
  };

  const handleCancel = () => {
    analytics.track('Clicked Cancel Live Transcription', {
      email: user?.email,
    });
    setEditingIndex(null);
    setEditText('');
  };

  const handleDelete = (index) => {
    analytics.track('Clicked Delete Live Non-Diarized Transcription Segment', {
      email: user?.email,
    });
    const newTranscript = fullTranscript.filter((_, i) => i !== index);
    onEditTranscription('live', newTranscript);
    setFullTranscript(newTranscript);
    // Close edit mode if the deleted item was being edited
    if (editingIndex === index) {
      setEditingIndex(null);
      setEditText('');
    }
  };

  useEffect(() => {
    if (summaryEvidenceDiv.current && selectedEvidences?.length > 0) {
      summaryEvidenceDiv.current.scrollIntoView();
    }
  }, [selectedEvidences?.length]);

  return (
    <>
      {fullTranscript.map((transcript, index) => {
        const selectedEvidence = selectedEvidences.find((selectedEvidence) => {
          const timestampMinute = moment(selectedEvidence.timestamp)
            .tz(browserTimezone)
            .format('YYYY-MM-DDTHH:mm');
          return timestampMinute === transcript.timestamp;
        });
        const isSelectedEvidence = !isEmpty(selectedEvidence);
        isFirstSummaryEvidence = isSelectedEvidence && !isFirstSummaryEvidence;

        return (
          <div
            key={index}
            className="text-gray-600 text-xs"
            id="live-transcription"
          >
            <div className="flex justify-between items-start">
              <strong>
                {moment(transcript.timestamp)
                  .tz(browserTimezone)
                  .format('h:mma')}
              </strong>
              {canEditTranscription && (
                <div className="flex gap-1">
                  {editingIndex === index ? (
                    <>
                      <button
                        onClick={() => handleSave(index)}
                        className="text-[#00D090] hover:text-[#059669]"
                      >
                        <CheckOutlined />
                      </button>
                      <button
                        onClick={handleCancel}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <CloseOutlined />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handleEdit(index, transcript.text)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <EditOutlined />
                    </button>
                  )}
                  {editingIndex !== index && (
                    <button
                      onClick={() => handleDelete(index)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <DeleteOutlined />
                    </button>
                  )}
                </div>
              )}
            </div>
            <br />
            {index === 0 ? (
              <>
                {editingIndex === index ? (
                  <Input.TextArea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    autoSize
                    className="flex-grow text-xs"
                  />
                ) : isSelectedEvidence ? (
                  <div
                    className="selected-evidence-wrapper"
                    ref={isFirstSummaryEvidence ? summaryEvidenceDiv : null}
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtmlContent(selectedEvidence.text),
                    }}
                  />
                ) : (
                  <div className="whitespace-pre-wrap">{transcript.text}</div>
                )}
                <span className="text-gray-400"> {livePartialTranscript}</span>
              </>
            ) : (
              <>
                {editingIndex === index ? (
                  <Input.TextArea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    autoSize
                    className="flex-grow text-xs"
                  />
                ) : isSelectedEvidence ? (
                  <div
                    ref={isFirstSummaryEvidence ? summaryEvidenceDiv : null}
                    className="selected-evidence-wrapper"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtmlContent(selectedEvidence.text),
                    }}
                  />
                ) : (
                  <div className="whitespace-pre-wrap">{transcript.text}</div>
                )}
              </>
            )}
            <br />
            <br />
          </div>
        );
      })}
      {!fullTranscript?.length && !livePartialTranscript?.length ? (
        <div className="text-gray-400">{placeholder}</div>
      ) : null}
    </>
  );
};

const TranscriptionExpanded = ({
  fullTranscript,
  setFullTranscript,
  livePartialTranscript,
  browserTimezone,
  placeholder,
  selectedEvidences,
  onEditTranscription,
  encounter,
  encounterPhase,
  encounterStatus,
  transcriptionMode,
  initTranscript,
  storedParams,
}) => {
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const { isEntitled: uploadAudioAllower } = useFeatureEntitlements(
    'upload-audio-recording'
  );
  const { prerecordedTranscript, prerecordedTranscriptSpeakers } = useSelector(
    (state) => state?.encounterState
  );

  const dispatch = useDispatch();
  const { fileNameResponse } = useSelector((state) => state?.audioState);
  useEffect(() => {
    dispatch(getFileNameSlice([]));
    if (encounter?.encounter_id) {
      dispatch(getFileName(encounter?.encounter_id, encounterPhase));
    }
  }, [dispatch, encounter?.encounter_id, encounterPhase]);

  return (
    <div className="p-4">
      <div className="flex justify-end gap-2 mb-2">
        {uploadAudioAllower && encounterPhase === 'in-visit' && (
          <Tooltip
            placement="left"
            title={
              fileNameResponse?.length > 0 ? (
                encounterStatus === 'new' ? (
                  <div>
                    Uploaded file: {fileNameResponse?.[0]?.['display_name']}
                  </div>
                ) : (
                  <div>
                    Summary generated from uploaded file.
                    <div>
                      Uploaded file: {fileNameResponse?.[0]?.['display_name']}
                    </div>
                  </div>
                )
              ) : encounterStatus !== 'new' ? (
                <div>Live transcript</div>
              ) : (
                ''
              )
            }
          >
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsUploadModalVisible(true)}
                disabled={
                  !(encounterStatus === 'new' && encounterPhase === 'in-visit')
                }
                className={`px-3 py-1 text-white rounded-xl ${
                  encounterStatus === 'new' && encounterPhase === 'in-visit'
                    ? 'bg-[#00D090] hover:bg-[#059669]'
                    : 'bg-gray-400 cursor-not-allowed'
                }`}
              >
                <UploadOutlined /> Upload Audio
              </button>
            </div>
          </Tooltip>
        )}
      </div>

      <AudioUploadModal
        visible={isUploadModalVisible}
        initTranscript={initTranscript}
        onClose={() => setIsUploadModalVisible(false)}
        encounterId={encounter?.encounter_id}
        encounterPhase={encounterPhase}
        storedParams={storedParams}
      />

      {transcriptionMode === 'prerecorded_diarized' ? (
        <PrerecordedTranscriptionDiarizedView
          prerecordedTranscript={prerecordedTranscript}
          prerecordedTranscriptSpeakers={prerecordedTranscriptSpeakers}
          browserTimezone={browserTimezone}
          placeholder={placeholder}
          selectedEvidences={selectedEvidences}
          onEditTranscription={onEditTranscription}
        />
      ) : transcriptionMode === 'live_diarized' ? (
        <LiveTranscriptionDiarizedView
          fullTranscript={fullTranscript}
          setFullTranscript={setFullTranscript}
          livePartialTranscript={livePartialTranscript}
          liveTranscriptSpeakers={prerecordedTranscriptSpeakers}
          browserTimezone={browserTimezone}
          placeholder={placeholder}
          selectedEvidences={selectedEvidences}
          onEditTranscription={onEditTranscription}
        />
      ) : transcriptionMode === 'live_non_diarized' ? (
        <LiveTranscriptionNonDiarizedView
          fullTranscript={fullTranscript}
          setFullTranscript={setFullTranscript}
          livePartialTranscript={livePartialTranscript}
          browserTimezone={browserTimezone}
          placeholder={placeholder}
          selectedEvidences={selectedEvidences}
          onEditTranscription={onEditTranscription}
        />
      ) : null}
    </div>
  );
};

const LiveTranscriptionCollapsed = ({ isCollapsed, setIsCollapsed }) => {
  return (
    <div className="current bg-white shadow-xl rounded-xl overflow-y-auto">
      <div
        className="flex justify-start text-black text-md font-bold p-3  border-b border-bottom-[#00d091]"
        style={{ paddingTop: '14px' }}
      >
        <button onClick={() => setIsCollapsed(!isCollapsed)}>
          <RightPanelOpenIcon />
        </button>
      </div>
      <div className="p-4"></div>
    </div>
  );
};

export const EncounterRightSidePanel = forwardRef(
  (
    {
      activeTab,
      isTranscribing,
      setActiveTab,
      fullTranscript,
      setFullTranscript,
      liveTranscript,
      livePartialTranscript,
      setLivePartialTranscript,
      encounterPhase,
      encounterStatus,
      summaryEvidences,
      resetSummaryEvidence,
      onEditTranscription,
      encounter,
      initTranscript,
      storedParams,
    },
    ref
  ) => {
    const placeholder = 'Transcripts will appear in real-time here.';
    const browserTimezone = moment.tz.guess();
    const [isCollapsed, setIsCollapsed] = useState(false); // State to control the collapse
    const [tabs, setTabs] = useState([]);

    const { isEntitled: hasICD10Access } = useFeatureEntitlements('icd-10');
    const { isEntitled: hasCPTAccess } = useFeatureEntitlements('cpt');
    const { isEntitled: isEncountersNoteHistoryEnabled } =
      useFeatureEntitlements('encounters-note-evidence');
    const { isEntitled: hasAudioPlayerAccess } =
      useFeatureEntitlements('audio-player');
    const { isEntitled: isLiveDiarizationEnabled } =
      useFeatureEntitlements('live-diarization');
    const dispatch = useDispatch();
    useEffect(() => {
      if (encounterStatus === 'completed') {
        dispatch(getEncounterAudioSlice(null));
        const fetchAudio = getAudio(encounter?.encounter_id, encounterPhase);
        fetchAudio(dispatch);
      } else {
        dispatch(getEncounterAudioSlice(null));
      }
    }, [encounter?.encounter_id, encounterStatus, encounterPhase, dispatch]);

    const { encounterAudio } = useSelector((state) => state?.audioState);
    const { prerecordedTranscript, prerecordedTranscriptSpeakers } =
      useSelector((state) => state?.encounterState);
    useEffect(() => {
      resetSummaryEvidence();

      return resetSummaryEvidence;
    }, [resetSummaryEvidence, activeTab]);

    useEffect(() => {
      resetSummaryEvidence();

      return resetSummaryEvidence;
    }, [resetSummaryEvidence, activeTab]);

    const updateTabs = () => {
      const tabs = [{ label: 'Transcript', value: 'live' }];

      if (encounterPhase === 'in-visit') {
        tabs.push({
          label: hasICD10Access ? (
            <div className="flex items-center">
              <span className="mr-1 ml-1">ICD-10</span> <ProIcon />
            </div>
          ) : (
            <div className="flex">
              <UpgradeTooltip>
                <span className="mr-1 ml-1">ICD-10</span> <LockIcon />
              </UpgradeTooltip>
            </div>
          ),
          value: 'icd10',
        });

        if (hasCPTAccess) {
          tabs.push({
            label: (
              <div className="flex items-center">
                <span className="mr-1 ml-1">CPT</span> <ProIcon />
              </div>
            ),
            value: 'cpt',
          });
        }
      }

      setTabs(tabs);
    };

    useEffect(() => {
      updateTabs();
    }, [encounterPhase, hasICD10Access, hasCPTAccess]);

    useEffect(() => {
      let combinedTranscripts;

      if (isTranscribing) {
        // When actively transcribing, filter out potential duplicates
        const newLiveTranscripts = liveTranscript.filter((liveItem) => {
          const timestampMinute = moment(liveItem.timestamp)
            .tz(browserTimezone)
            .format('YYYY-MM-DDTHH:mm:ss');

          // Check if this item already exists in fullTranscript
          return !fullTranscript.some(
            (fullItem) =>
              moment(fullItem.timestamp)
                .tz(browserTimezone)
                .format('YYYY-MM-DDTHH:mm:ss') === timestampMinute &&
              fullItem.speaker === liveItem.speaker &&
              fullItem.text.includes(liveItem.text)
          );
        });

        // Only proceed if there are new unique items
        if (newLiveTranscripts.length === 0) return;

        combinedTranscripts = [...fullTranscript, ...newLiveTranscripts];
      } else {
        // When not transcribing (processing final results), use original approach
        combinedTranscripts = [...fullTranscript, ...liveTranscript];
      }

      const groupedTranscripts = combinedTranscripts.reduce((acc, current) => {
        // Format timestamp to the minute in browser timezone
        const timestampMinute = moment(current.timestamp)
          .tz(browserTimezone)
          .format('YYYY-MM-DDTHH:mm:ss');

        // Find if there's already an entry for this timestamp and speaker
        const existingEntryIndex = acc.findIndex(
          (entry) =>
            entry.timestamp === timestampMinute &&
            entry.speaker === current.speaker
        );

        if (existingEntryIndex !== -1) {
          // Only append if the text isn't already included when transcribing
          // Otherwise just append as in the original code
          if (
            isTranscribing &&
            acc[existingEntryIndex].text.includes(current.text)
          ) {
            // Skip if already included during active transcription
          } else {
            acc[existingEntryIndex].text += ` ${current.text}`;
          }
        } else {
          // If it's a new minute or a new speaker, push a new entry
          acc.push({
            timestamp: timestampMinute,
            text: current.text,
            speaker: current.speaker,
          });
        }
        return acc;
      }, []);

      // Sort the transcripts in descending order of timestamp
      groupedTranscripts.sort(
        (a, b) =>
          moment(b.timestamp).tz(browserTimezone).valueOf() -
          moment(a.timestamp).tz(browserTimezone).valueOf()
      );

      // Update the state to hold the grouped and sorted transcripts
      if (groupedTranscripts?.length > 0) {
        setFullTranscript(groupedTranscripts);
        setLivePartialTranscript('');
      }
    }, [
      browserTimezone,
      encounter?.encounter_id,
      liveTranscript,
      encounterPhase,
    ]);
    const getTranscriptionMode = () => {
      if (isLiveDiarizationEnabled) {
        return 'live_diarized';
      }
      if (
        prerecordedTranscript?.length > 0 &&
        encounterStatus === 'completed'
      ) {
        if (prerecordedTranscript.some((t) => t.speaker !== null)) {
          return 'prerecorded_diarized';
        }
      }
      return 'live_non_diarized';
    };

    return isCollapsed && encounterPhase !== 'vca-summary' ? (
      <LiveTranscriptionCollapsed
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
      />
    ) : (
      <div
        className={
          encounterPhase === 'vca-summary'
            ? 'hidden'
            : 'w-1/4 h-full current bg-white shadow-xl rounded-xl flex flex-col'
        }
      >
        <div className="flex flex-0 w-full border-b border-gray-200 p-2">
          <button
            className="pr-2"
            id="RightPanelCloseIcon"
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            <RightPanelCloseIcon />
          </button>
          <div className="flex justify-between">
            <SegmentedTabs
              options={tabs}
              activeTab={activeTab}
              setActiveTab={(tab) => setActiveTab(tab)}
            />
          </div>
        </div>
        {activeTab === 'live' ? (
          <div className="flex flex-col h-full justify-between overflow-y-auto">
            <TranscriptionExpanded
              transcriptionMode={getTranscriptionMode()}
              fullTranscript={fullTranscript}
              setFullTranscript={setFullTranscript}
              liveTranscript={liveTranscript}
              livePartialTranscript={livePartialTranscript}
              placeholder={placeholder}
              browserTimezone={browserTimezone}
              selectedEvidences={
                isEncountersNoteHistoryEnabled && !isEmpty(summaryEvidences)
                  ? summaryEvidences
                  : []
              }
              onEditTranscription={onEditTranscription}
              encounter={encounter}
              encounterPhase={encounterPhase}
              encounterStatus={encounterStatus}
              initTranscript={initTranscript}
              storedParams={storedParams}
            />
            {encounterAudio != null &&
            hasAudioPlayerAccess &&
            encounter?.encounter_id ? (
              <div
                style={{ marginTop: 10 }}
                className="sticky bottom-0 bg-white p-2"
              >
                <AudioPlayer
                  audioUrl={encounterAudio}
                  onPlay={() =>
                    analytics.track('audio_player_started', {
                      encounter_id: encounter.encounter_id,
                      encounter_phase: encounterPhase,
                    })
                  }
                />
              </div>
            ) : null}
          </div>
        ) : activeTab === 'icd10' ? (
          <div className="h-full overflow-y-auto">
            <ICD10Coding
              ref={ref}
              encounterStatus={encounterStatus}
              allowed={hasICD10Access}
            />
          </div>
        ) : activeTab === 'cpt' ? (
          <div className="h-full overflow-y-auto">
            <CPTCodes
              encounterStatus={encounterStatus}
              allowed={hasCPTAccess}
            />
          </div>
        ) : null}
      </div>
    );
  }
);
